import React from 'react';
import { useAlert } from '../../../AlertProvider';
import { Formik } from 'formik';
import { Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useQueryClient } from '@tanstack/react-query';

import { CancelButton, FormModal, SubmitButton } from '../../../Form';
import { ModalActions, ModalContent } from '../../../Modal';
import { Button } from '../../../Button';
import { useArchiveMatch } from './CareTeamList.hooks';


type ArchiveMatchModalProps = {
  open: boolean;
  onClose: () => void;
  matchId: string;
  patientId?: string;
  providerFullName?: string;
};


const ArchiveMatchModal: React.FC<ArchiveMatchModalProps> = ({ open, onClose, matchId, providerFullName, patientId }) => {
  const alert = useAlert();
  const queryClient = useQueryClient();
  const { archiveMatch } = useArchiveMatch();

  return (
    <Formik
      enableReinitialize
      initialValues={{
        matchId: matchId,
      }}
      onSubmit={async values => {
        await archiveMatch({ matchId: values.matchId });
        await queryClient.invalidateQueries([ 'user', patientId, 'matches' ]);
        alert.success(<strong>Match archived and provider notified</strong>);
        onClose();
      }}
    >
      <FormModal
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        title="Archive Match?"
        showCloseIcon={false}
      >
        <ModalContent>
          <Typography>
            If you archive this match, all future sessions with {providerFullName} will be canceled, and the provider won’t be able to schedule any more sessions.
          </Typography>
        </ModalContent>
        <ModalActions>
          <CancelButton onClick={onClose}>Cancel</CancelButton>
          <SubmitButton variant="contained" color="error">Archive Provider</SubmitButton>
        </ModalActions>
      </FormModal>
    </Formik>
  );
};


const useStyles = makeStyles()({
  archiveButton: {
    minWidth: '130px',
  },
  archivedButton: {
    minWidth: '130px',
    textTransform: 'none',
    textAlign: 'center',
    fontStyle: 'italic',
  }
});


export type ArchiveMatchButtonProps = {
  matchId: string;
  archived: boolean;
  patientId?: string;
  providerFullName?: string;
};


export const ArchiveMatchButton: React.FC<ArchiveMatchButtonProps> = (props) => {
  const { classes } = useStyles();
  const [ isArchiveModalOpen, setIsArchiveModalOpen ] = React.useState(false);

  if (props.archived) {
    return (
      <Button className={classes.archivedButton} disabled>
        Archived
      </Button>
    );
  }

  return (
    <>
      <Button
        size="large"
        color="secondary"
        className={classes.archiveButton}
        onClick={() => setIsArchiveModalOpen(true)}
      >
        Archive
      </Button>
      <ArchiveMatchModal
        open={isArchiveModalOpen}
        onClose={() => setIsArchiveModalOpen(false)}
        matchId={props.matchId}
        patientId={props.patientId}
        providerFullName={props.providerFullName}
      />
    </>
  );
};
